// client/src/pages/MapView/MapView.js
// Leaflet-karta centrerad på Sverige med mobil-först design (kart-only på
// mobil, full controls på desktop). Initierar Leaflet-default-markers.
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import API_CONFIG from '../../config/apiConfig';
import './MapView.css';

// Fix Leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});
const MapView = () => {
  // ✅ Detect mobile device
  const [isMobile, setIsMobile] = useState(false);
  
  // Core state
  const [selectedWeek, setSelectedWeek] = useState(53);
  const [selectedSymptoms, setSelectedSymptoms] = useState([]);
  const [selectedPostalCode, setSelectedPostalCode] = useState(null);
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Enhanced visualization state
  const [heatmapLayer, setHeatmapLayer] = useState(null);
  const [mapInstance, setMapInstance] = useState(null);
  const [visualizationMode, setVisualizationMode] = useState('professional');
  const [heatmapIntensity, setHeatmapIntensity] = useState(0.6);
  const [heatmapRadius, setHeatmapRadius] = useState(25);
  const [mapZoom, setMapZoom] = useState(6);
  
  // ✅ Mobile interaction state
  const [isMobileInteracting, setIsMobileInteracting] = useState(false);
  
  // Multi-level settings
  const [showMarkers, setShowMarkers] = useState(false);
  const [autoZoomAdapt, setAutoZoomAdapt] = useState(true);
  const [heatmapQuality, setHeatmapQuality] = useState('high');

  // Sweden coordinates - Optimized for perfect view
  const swedenCenter = useMemo(() => [62.2, 16.5], []);
  const swedenZoom = useMemo(() => {
    if (typeof isMobile === 'undefined') return 5; // ✅ LESS zoom = more of Sweden visible
    return isMobile ? 5 : 5; // ✅ Same zoom for both (was 6 for desktop)
  }, [isMobile]);

  // Available symptoms
  const availableSymptoms = useMemo(() => [
    'Feber', 'Hosta', 'Andningssvårigheter', 'Trötthet', 
    'Huvudvärk', 'Muskelvärk', 'Halsont', 'Snuva', 'Frossa',
    'Illamående', 'Diarré', 'Yrsel', 'Bröstsmärta', 'Aptitlöshet'
  ], []);

  // Available weeks
  const availableWeeks = useMemo(() => 
    Array.from({length: 14}, (_, i) => i + 40), 
  []);

  // Ultra-safe cleanup on unmount
  useEffect(() => {
    return () => {
      if (heatmapLayer && mapInstance) {
        try {
          if (mapInstance.getContainer && mapInstance.getContainer()) {
            mapInstance.removeLayer(heatmapLayer);
          }
        } catch (error) {
          // Silent cleanup failure - component is unmounting anyway
        }
      }
    };
  }, [heatmapLayer, mapInstance]);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Mobile optimization - simplified settings
  useEffect(() => {
    if (isMobile) {
      setHeatmapQuality('medium'); // Better performance on mobile
      setAutoZoomAdapt(true);
      setShowMarkers(false); // Start with heatmap only on mobile
    }
  }, [isMobile]);

  // Load Leaflet Heat plugin
  useEffect(() => {
    const loadHeatmapPlugin = async () => {
      try {
        if (!window.L || !window.L.heatLayer) {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet.heat/0.2.0/leaflet-heat.js';
          script.async = true;
          
          // Fix Canvas2D performance warning
          script.onload = () => {
            if (window.L && window.L.heatLayer) {
              // Override canvas creation to add willReadFrequently
              const originalHeatLayer = window.L.heatLayer;
              window.L.heatLayer = function(latlngs, options) {
                const layer = originalHeatLayer.call(this, latlngs, options);
                
                // Fix canvas performance warning
                const originalOnAdd = layer.onAdd;
                layer.onAdd = function(map) {
                  originalOnAdd.call(this, map);
                  // ✅ ENHANCED: Fix Canvas2D performance warning
                  if (this._canvas && this._canvas.getContext) {
                    const ctx = this._canvas.getContext('2d', { willReadFrequently: true });
                    // Force canvas optimization
                    this._canvas.style.willReadFrequently = 'true';
                  }
                };
                
                return layer;
              };
            }
          };
          
          document.head.appendChild(script);
        }
      } catch (error) {
        // Silent fallback
      }
    };

    loadHeatmapPlugin();
  }, []);

  // Fetch health data (enhanced with mobile optimization)
  useEffect(() => {
    const fetchMapData = async () => {
      if (!selectedWeek) return;
      
      setLoading(true);
      setError(null);
      
      try {
        // Mobile gets fewer data points for better performance
        const limit = isMobile ? 100 : 200;
        let url = `${API_CONFIG.baseUrl}/health-data/postal-groups?week=${selectedWeek}&limit=${limit}`;
        
        if (selectedSymptoms.length > 0) {
          url += `&symptoms=${encodeURIComponent(selectedSymptoms.join(','))}&matchMode=single`;
}
        
        const response = await fetch(url);
        const result = await response.json();
        
        if (result.success && result.data) {
          const enhancedData = result.data.map(item => ({
            ...item,
            heatIntensity: calculateHeatIntensity(item.count || 0, result.data),
            riskLevel: calculateRiskLevel(item.count || 0),
            markerSize: calculateMarkerSize(item.count || 0, mapZoom, isMobile)
          }));
          
          setMapData(enhancedData);
        } else {
          throw new Error(result.error || 'Failed to fetch map data');
        }
        
      } catch (err) {
        setError(err.message);
        setMapData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchMapData();
  }, [selectedWeek, selectedSymptoms, mapZoom, isMobile]);

  // Calculate heat intensity
  const calculateHeatIntensity = (count, allData) => {
    if (!allData || allData.length === 0) return 0;
    
    const maxCount = Math.max(...allData.map(item => item.count || 0));
    const percentile = count / maxCount;
    
    return Math.pow(percentile, 0.7);
  };

  // Calculate risk level
  const calculateRiskLevel = (count) => {
    if (count >= 100) return { level: 'critical', color: '#c0392b', icon: '🔥🔥🔥🔥' };
    if (count >= 50) return { level: 'high', color: '#e74c3c', icon: '🔥🔥🔥' };
    if (count >= 20) return { level: 'medium', color: '#f39c12', icon: '🔥🔥' };
    if (count >= 10) return { level: 'low', color: '#f1c40f', icon: '🔥' };
    if (count >= 5) return { level: 'minimal', color: '#5dade2', icon: '💙' };
    return { level: 'very-low', color: '#3498db', icon: '💙' };
  };

  // Calculate marker size (mobile-optimized)
  const calculateMarkerSize = (count, zoom, isMobile = false) => {
    const baseSize = isMobile ? 
      Math.max(zoom - 2, 1) * 1.5 : // Smaller on mobile
      Math.max(zoom - 4, 1) * 2;
    
    const countMultiplier = Math.sqrt(count / 10);
    const maxSize = isMobile ? 30 : 40; // Smaller max on mobile
    
    return Math.min(Math.max(baseSize + countMultiplier * 3, 8), maxSize);
  };

  // Enhanced heatmap creation (Production-ready: No debug output)
  useEffect(() => {
    // Multiple safety checks before any map operations
    if (!mapInstance || 
        !mapInstance.getContainer || 
        !mapInstance.getContainer() ||
        !window.L || 
        !window.L.heatLayer || 
        mapData.length === 0) {
      return;
    }

    // ✅ Additional safety: Check if map is in a valid state
    const container = mapInstance.getContainer();
    if (!container || !container.offsetParent) {
      return;
    }

    // Remove existing heatmap safely
    if (heatmapLayer) {
      try {
        mapInstance.removeLayer(heatmapLayer);
        setHeatmapLayer(null);
      } catch (error) {
        setHeatmapLayer(null); // Clear state anyway
      }
    }

    if (visualizationMode === 'markers') {
      return;
    }

    // ✅ Delay heatmap creation to ensure map is stable
    const createHeatmapSafely = () => {
      try {
        const heatPoints = mapData
          .filter(item => item.position && Array.isArray(item.position) && item.position.length === 2)
          .map(item => {
            const [lat, lng] = item.position;
            let intensity = item.heatIntensity || 0;
            
            if (autoZoomAdapt) {
              if (mapZoom <= 6) {
                intensity = Math.min(intensity * 1.5, 1.0);
              } else if (mapZoom <= 9) {
                intensity = Math.min(intensity * 1.2, 1.0);
              } else {
                intensity = Math.min(intensity * 0.9, 1.0);
              }
            }
            
            const finalIntensity = Math.min(intensity * heatmapIntensity * 2, 1.0);
            return [lat, lng, finalIntensity];
          });

        if (heatPoints.length === 0) {
          return;
        }

        // Mobile-optimized heatmap settings
        const mobileSettings = {
          radius: Math.max(heatmapRadius - 10, 15),
          blur: 10,
          max: 0.7,
          minOpacity: 0.3
        };

        const desktopSettings = {
          high: {
            radius: heatmapRadius,
            blur: 15,
            max: 0.8,
            minOpacity: 0.4
          },
          medium: {
            radius: Math.max(heatmapRadius - 5, 15),
            blur: 12,
            max: 0.7,
            minOpacity: 0.3
          },
          performance: {
            radius: Math.max(heatmapRadius - 10, 10),
            blur: 8,
            max: 0.6,
            minOpacity: 0.2
          }
        };

        const settings = isMobile ? mobileSettings : desktopSettings[heatmapQuality];

        const enhancedGradient = {
          0.0: '#3498db',
          0.15: '#5dade2',
          0.3: '#f1c40f',
          0.5: '#f39c12',
          0.7: '#e74c3c',
          0.85: '#c0392b',
          1.0: '#8e44ad'
        };

        // ✅ Check map is still valid before adding layer
        if (!mapInstance.getContainer()) {
          return;
        }

        const newHeatmapLayer = window.L.heatLayer(heatPoints, {
          ...settings,
          gradient: enhancedGradient
        });

        newHeatmapLayer.addTo(mapInstance);
        setHeatmapLayer(newHeatmapLayer);

      } catch (error) {
        setHeatmapLayer(null);
      }
    };

    // Use requestAnimationFrame for proper timing
    requestAnimationFrame(createHeatmapSafely);

  }, [mapInstance, mapData, visualizationMode, heatmapIntensity, heatmapRadius, heatmapQuality, mapZoom, autoZoomAdapt, isMobile]);

  // Smart marker visibility
  useEffect(() => {
    if (autoZoomAdapt) {
      setShowMarkers(mapZoom >= (isMobile ? 7 : 8) || visualizationMode === 'markers');
    }
  }, [mapZoom, visualizationMode, autoZoomAdapt, isMobile]);

  // Enhanced marker component (mobile-optimized)
  const EnhancedMarker = ({ item, onClick }) => {
    if (!item.position || !Array.isArray(item.position) || item.position.length !== 2) {
      return null;
    }

    const [lat, lng] = item.position;
    const count = item.count || 0;
    const risk = item.riskLevel || calculateRiskLevel(count);
    const size = item.markerSize || calculateMarkerSize(count, mapZoom, isMobile);

    const customIcon = L.divIcon({
      className: 'enhanced-health-marker',
      html: `<div style="background:linear-gradient(135deg,${risk.color},${risk.color}dd);width:${size}px;height:${size}px;border-radius:50%;border:${isMobile ? '2px' : '3px'} solid white;box-shadow:0 ${isMobile ? '2px 6px' : '3px 10px'} rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:white;font-size:${Math.min(size/3, isMobile ? 12 : 14)}px;font-weight:bold;cursor:pointer">${count}</div>`,
      iconSize: [size + (isMobile ? 4 : 6), size + (isMobile ? 4 : 6)],
      iconAnchor: [size/2 + (isMobile ? 2 : 3), size/2 + (isMobile ? 2 : 3)]
    });

    return (
      <Marker
        position={[lat, lng]}
        icon={customIcon}
        eventHandlers={{
          click: () => onClick(item.postalCode)
        }}
      >
        <Popup>
          <div className="enhanced-marker-popup">
            <h4>📮 {item.postalCode}</h4>
            <p><strong>🏙️ Stad:</strong> {item.city || 'Unknown'}</p>
            <p><strong>📊 Rapporter:</strong> {count}</p>
            <p><strong>🔥 Risknivå:</strong> {risk.icon} {risk.level}</p>
            {!isMobile && (
              <p><strong>🎯 Heat Intensity:</strong> {Math.round((item.heatIntensity || 0) * 100)}%</p>
            )}
            
            {item.symptoms && item.symptoms.length > 0 && (
              <div>
                <strong>🩺 Symptom:</strong>
                <ul style={{ margin: '5px 0', paddingLeft: '15px', maxHeight: isMobile ? '80px' : '100px', overflowY: 'auto' }}>
                  {item.symptoms.slice(0, isMobile ? 3 : 5).map((symptom, idx) => (
                    <li key={idx} style={{ fontSize: isMobile ? '11px' : '12px' }}>
                      {typeof symptom === 'string' ? symptom : symptom.name}
                    </li>
                  ))}
                  {item.symptoms.length > (isMobile ? 3 : 5) && (
                    <li style={{ fontSize: isMobile ? '11px' : '12px', fontStyle: 'italic' }}>
                      +{item.symptoms.length - (isMobile ? 3 : 5)} fler...
                    </li>
                  )}
                </ul>
              </div>
            )}
            
            <hr style={{ margin: '6px 0' }} />
            <small style={{ color: '#7f8c8d', fontSize: isMobile ? '10px' : '11px' }}>
              Vecka {selectedWeek} • Zoom {mapZoom} • Sverige
            </small>
          </div>
        </Popup>
      </Marker>
    );
  };

  // Handle postal code selection (Production-ready: Safe map operations)
  const handlePostalCodeClick = (postalCode) => {
    setSelectedPostalCode(postalCode);
    
    const selectedData = mapData.find(item => item.postalCode === postalCode);
    if (selectedData && selectedData.position && mapInstance && mapInstance.getContainer()) {
      try {
        const [lat, lng] = selectedData.position;
        mapInstance.setView([lat, lng], Math.max(mapZoom, isMobile ? 8 : 10), { animate: true });
      } catch (error) {
        // Silent fallback: just set the selected postal code without map movement
      }
    }
  };

  // ✅ Handle symptom selection (mobile-optimized)
  const handleSymptomToggle = (symptom) => {
    setSelectedSymptoms(prev => {
      if (prev.includes(symptom)) {
        return prev.filter(s => s !== symptom);
      } else {
        // Mobile: limit to 2 symptoms for performance
        if (isMobile && prev.length >= 2) {
          return [prev[prev.length - 1], symptom];
        }
        return [...prev, symptom];
      }
    });
  };

  // ✅ Mobile interaction handlers
  const handleMobileMapStart = useCallback(() => {
    if (isMobile) {
      setIsMobileInteracting(true);
    }
  }, [isMobile]);

  const handleMobileMapEnd = useCallback(() => {
    if (isMobile) {
      // Delay showing symptom selector until interaction is done
      setTimeout(() => {
        setIsMobileInteracting(false);
      }, 500);
    }
  }, [isMobile]);

  // ✅ Handle map events (Production-ready: Ultra-safe Leaflet initialization)
  const handleMapReady = useCallback((map) => {
    const mapInstance = map.target;
    
    // ✅ Multiple safety checks
    const safelyInitializeMap = () => {
      if (!mapInstance || !mapInstance.getContainer()) {
        return false;
      }
      
      const container = mapInstance.getContainer();
      if (!container || !container.offsetParent) {
        return false;
      }
      
      try {
        setMapInstance(mapInstance);
        
        // ✅ Mobile interaction listeners
        if (isMobile) {
          mapInstance.on('zoomstart', handleMobileMapStart);
          mapInstance.on('zoomend', handleMobileMapEnd);
          mapInstance.on('dragstart', handleMobileMapStart);
          mapInstance.on('dragend', handleMobileMapEnd);
          mapInstance.on('movestart', handleMobileMapStart);
          mapInstance.on('moveend', handleMobileMapEnd);
        }
        
        // ✅ Direct setView without invalidateSize
        requestAnimationFrame(() => {
          try {
            if (mapInstance.getContainer()) {
              mapInstance.setView(swedenCenter, swedenZoom, { animate: false });
            }
          } catch (viewError) {
            // Silent fallback - continue without auto-centering
          }
        });
        
        return true;
      } catch (error) {
        return false;
      }
    };
    
    // ✅ Progressive retry with increasing delays
    const tryInitialize = (attempt = 1, maxAttempts = 5) => {
      const success = safelyInitializeMap();
      
      if (!success && attempt < maxAttempts) {
        const delay = attempt * 50;
        setTimeout(() => tryInitialize(attempt + 1, maxAttempts), delay);
      } else if (!success) {
        // Set map instance anyway to prevent hanging
        setMapInstance(mapInstance);
      }
    };
    
    // ✅ Start initialization with proper timing
    if (document.readyState === 'complete') {
      tryInitialize();
    } else {
      window.addEventListener('load', () => tryInitialize(), { once: true });
    }
    
  }, [isMobile, swedenCenter, swedenZoom, handleMobileMapStart, handleMobileMapEnd]);

  const handleMapZoom = useCallback((e) => {
    if (e.target && e.target.getZoom) {
      const newZoom = e.target.getZoom();
      setMapZoom(newZoom);
    }
  }, []);

  // Get selected postal data
  const selectedPostalData = selectedPostalCode 
    ? mapData.find(item => item.postalCode === selectedPostalCode)
    : null;

  // ✅ Determine what to show
  const shouldShowHeatmap = visualizationMode === 'professional' || visualizationMode === 'hybrid';
  const shouldShowMarkersNow = showMarkers && (visualizationMode === 'markers' || visualizationMode === 'hybrid');

  // MOBILE: Return map-only interface
  if (isMobile) {
    return (
      <div className="map-view-container mobile-map-only">
        
        {/* Mobile: Minimal floating controls */}
        <div className="mobile-floating-controls">
          <select 
            value={selectedWeek} 
            onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
            className="mobile-week-selector"
          >
            {availableWeeks.map(week => (
              <option key={week} value={week}>V{week}</option>
            ))}
          </select>
          
          {selectedSymptoms.length > 0 && (
            <button 
              className="mobile-clear-btn"
              onClick={() => setSelectedSymptoms([])}
            >
              🗑️
            </button>
          )}
        </div>

        {/* Mobile: Enhanced symptom selector with ALL symptoms in 2 rows */}
        {availableSymptoms.length > 0 && (
          <div className={`mobile-symptom-selector ${isMobileInteracting ? 'hidden' : 'visible'}`}>
            <div className="mobile-symptom-scroll">
              {/* ✅ Show ALL symptoms on mobile (was limited to 12) */}
              {availableSymptoms.map(symptom => (
                <button
                  key={symptom}
                  className={`mobile-symptom-btn ${selectedSymptoms.includes(symptom) ? 'selected' : ''}`}
                  onClick={() => handleSymptomToggle(symptom)}
                >
                  {symptom}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Mobile: Full-screen map with interaction state */}
        <div className={`mobile-map-container ${isMobileInteracting ? 'interacting' : ''}`}>
          
          {loading && (
            <div className="mobile-loading">
              <div className="loading-spinner">🔄</div>
              <p>Laddar Sverige...</p>
            </div>
          )}

          {error && (
            <div className="mobile-error">
              <div className="error-icon">⚠️</div>
              <p>Fel: {error}</p>
            </div>
          )}

          {!loading && !error && (
            <MapContainer
              center={swedenCenter}
              zoom={swedenZoom}
              className="mobile-leaflet-map"
              scrollWheelZoom={true}
              whenReady={handleMapReady}
              onZoomEnd={handleMapZoom}
              style={{ height: '100%', width: '100%' }}
            >
              
              <TileLayer
                url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              />

              {shouldShowMarkersNow && mapData.map((item, index) => (
                <EnhancedMarker 
                  key={`mobile-marker-${item.postalCode}-${index}`}
                  item={item} 
                  onClick={handlePostalCodeClick}
                />
              ))}

            </MapContainer>
          )}

        </div>

        {/* Mobile: Minimal status */}
        <div className="mobile-status">
          🇸🇪 Vecka {selectedWeek} • {mapData.length} punkter • Zoom {mapZoom}
        </div>

      </div>
    );
  }

  // DESKTOP: Full interface
  return (
    <div className="map-view-container">
      
      {/* Desktop Header */}
      <div className="map-header">
        <h1>🗺️ Hälsokarta Sverige - Professionell Visualisering</h1>
        <p>✅ Centrerad på Sverige • Zoom-anpassad • Export-klar • Professionell kvalitet</p>
      </div>

      {/* Desktop Controls */}
      <div className="map-controls-panel">
        
        <div className="control-group">
          <label>📅 Vecka:</label>
          <select 
            value={selectedWeek} 
            onChange={(e) => setSelectedWeek(parseInt(e.target.value))}
            className="week-selector"
          >
            {availableWeeks.map(week => (
              <option key={week} value={week}>Vecka {week}</option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label>👁️ Visualisering:</label>
          <select 
            value={visualizationMode}
            onChange={(e) => setVisualizationMode(e.target.value)}
            className="mode-selector"
          >
            <option value="professional">🔥 Professionell Heatmap</option>
            <option value="markers">📍 Detaljmarkörer</option>
            <option value="hybrid">🔄 Heatmap + Markörer</option>
          </select>
        </div>

        <div className="control-group">
          <label>⚡ Kvalitet:</label>
          <select 
            value={heatmapQuality}
            onChange={(e) => setHeatmapQuality(e.target.value)}
            className="quality-selector"
          >
            <option value="high">🎯 Hög Kvalitet</option>
            <option value="medium">⚖️ Balanserad</option>
            <option value="performance">🚀 Prestanda</option>
          </select>
        </div>

        <div className="control-group">
          <label>
            <input
              type="checkbox"
              checked={autoZoomAdapt}
              onChange={(e) => setAutoZoomAdapt(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            🎯 Auto-anpassa zoom
          </label>
        </div>

        {shouldShowHeatmap && (
          <>
            <div className="control-group">
              <label>🔥 Intensitet: {Math.round(heatmapIntensity * 100)}%</label>
              <input
                type="range"
                min="0.2"
                max="1.0"
                step="0.1"
                value={heatmapIntensity}
                onChange={(e) => setHeatmapIntensity(parseFloat(e.target.value))}
                className="intensity-slider"
              />
            </div>
            
            <div className="control-group">
              <label>📏 Radie: {heatmapRadius}px</label>
              <input
                type="range"
                min="10"
                max="60"
                step="5"
                value={heatmapRadius}
                onChange={(e) => setHeatmapRadius(parseInt(e.target.value))}
                className="radius-slider"
              />
            </div>
          </>
        )}

        <div className="control-group">
          <label>🩺 Symptom ({selectedSymptoms.length} valda):</label>
          <div className="symptom-buttons">
            {availableSymptoms.map(symptom => (
              <button
                key={symptom}
                className={`symptom-btn ${selectedSymptoms.includes(symptom) ? 'selected' : ''}`}
                onClick={() => handleSymptomToggle(symptom)}
              >
                {symptom}
              </button>
            ))}
          </div>
        </div>

        <div className="control-group">
          <div className="action-buttons">
            {selectedSymptoms.length > 0 && (
              <button 
                className="clear-filters-btn"
                onClick={() => setSelectedSymptoms([])}
              >
                🗑️ Rensa Filter
              </button>
            )}
          </div>
        </div>

      </div>

      {/* Desktop Main Content */}
      <div className="map-main-content">
        
        <div className="map-container">
          
          {loading && (
            <div className="map-loading">
              <div className="loading-spinner">🔄</div>
              <p>Laddar Sverige heatmap för vecka {selectedWeek}...</p>
            </div>
          )}

          {error && (
            <div className="map-error">
              <div className="error-icon">⚠️</div>
              <p>Fel vid laddning: {error}</p>
              <button onClick={() => window.location.reload()}>Försök igen</button>
            </div>
          )}

          {!loading && !error && (
            <MapContainer
              center={swedenCenter}
              zoom={swedenZoom}
              className="leaflet-map"
              scrollWheelZoom={true}
              whenReady={handleMapReady}
              onZoomEnd={handleMapZoom}
              style={{ height: '100%', width: '100%' }}
            >
              
              <TileLayer
                url="https://cartodb-basemaps-{s}.global.ssl.fastly.net/light_all/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors, &copy; <a href="https://carto.com/attributions">CARTO</a>'
              />

              {shouldShowMarkersNow && mapData.map((item, index) => (
                <EnhancedMarker 
                  key={`desktop-marker-${item.postalCode}-${index}`}
                  item={item} 
                  onClick={handlePostalCodeClick}
                />
              ))}

            </MapContainer>
          )}

        </div>

        {/* Desktop Sidebar */}
        {selectedPostalData && (
          <div className="postal-sidebar">
            <div className="sidebar-header">
              <h3>📮 {selectedPostalData.postalCode}</h3>
              <button 
                className="close-sidebar"
                onClick={() => setSelectedPostalCode(null)}
              >
                ✕
              </button>
            </div>
            
            <div className="sidebar-content">
              <div className="detail-group">
                <h4>📊 Analys</h4>
                <p><strong>Stad:</strong> {selectedPostalData.city || 'Unknown'}</p>
                <p><strong>Rapporter:</strong> {selectedPostalData.count || 0}</p>
                <p><strong>Risknivå:</strong> {selectedPostalData.riskLevel?.icon} {selectedPostalData.riskLevel?.level}</p>
                <p><strong>Heat Intensitet:</strong> {Math.round((selectedPostalData.heatIntensity || 0) * 100)}%</p>
              </div>

              {selectedPostalData.symptoms && selectedPostalData.symptoms.length > 0 && (
                <div className="detail-group">
                  <h4>🩺 Rapporterade Symptom</h4>
                  <ul className="symptom-list">
                    {selectedPostalData.symptoms.map((symptom, index) => (
                      <li key={index}>
                        {typeof symptom === 'string' ? symptom : symptom.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="detail-group">
                <h4>🎯 Tekniska Detaljer</h4>
                <p><strong>Vecka:</strong> {selectedWeek}</p>
                <p><strong>Visualisering:</strong> {visualizationMode}</p>
                <p><strong>Zoom:</strong> {mapZoom}</p>
                {selectedPostalData.position && (
                  <p><strong>Position:</strong> {selectedPostalData.position[0].toFixed(4)}, {selectedPostalData.position[1].toFixed(4)}</p>
                )}
              </div>
            </div>
          </div>
        )}

      </div>

      {/* Desktop Status Bar */}
      <div className="map-status-bar">
        <span>🇸🇪 Sverige Heatmap • {mapData.length} datapunkter • Vecka {selectedWeek}</span>
        {selectedSymptoms.length > 0 && (
          <span> • Filtrerat: {selectedSymptoms.join(', ')}</span>
        )}
        <span> • Zoom: {mapZoom}</span>
        <span> • Centrerad på Sverige</span>
      </div>

    </div>
  );
};

export default MapView;